import React, { useState } from 'react'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
} from 'antd'
import moment from 'moment'
import './SearchConditions.css'
import type {
  PortfolioOption,
  SearchConditionErrors,
  SearchConditionRow,
  SearchConditionsDraft,
} from './types'

type Props = {
  value: SearchConditionsDraft
  errors: SearchConditionErrors
  portfolios: PortfolioOption[]
  portfolioSelectionDisabled: boolean
  searchDisabled: boolean
  conditionsDisabled?: boolean
  searchLoading?: boolean
  onChange: (value: SearchConditionsDraft) => void
  onAddRow: () => void
  onRemoveRow: (rowId: string) => void
  onSearch: () => void
  t: (key: string) => string
}

const ROW_STYLE = { flexDirection: 'row' as const }
const COLUMN_STYLE = { minWidth: 0 }
const INPUT_STYLE = { width: '100%', minWidth: 0 }
const BENCHMARK_STYLE = { width: 160, minWidth: 0 }
const OPTION_STYLE = { display: 'flex', alignItems: 'center', gap: 8 }
const BENCHMARK_ERROR_KEYS = {
  required: 'hkReport.error.benchmarkRequired',
  invalid: 'hkReport.error.benchmarkInvalid',
  range: 'hkReport.error.benchmarkRange',
  precision: 'hkReport.error.benchmarkPrecision',
}

const SearchConditions = ({
  value,
  errors,
  portfolios,
  portfolioSelectionDisabled,
  searchDisabled,
  conditionsDisabled = false,
  searchLoading = false,
  onChange,
  onAddRow,
  onRemoveRow,
  onSearch,
  t,
}: Props): JSX.Element => {
  const [dateMode, setDateMode] = useState<'Monthly' | 'AllDate'>('Monthly')
  const parsedDate = moment(value.baseDate, 'YYYY-MM-DD', true)
  const dateErrorKey = errors.baseDate
    ? errors.baseDate === 'required'
      ? 'hkReport.error.baseDateRequired'
      : 'hkReport.error.baseDateInvalid'
    : undefined
  const updateRow = (rowId: string, changes: Partial<SearchConditionRow>) =>
    onChange({
      ...value,
      rows: value.rows.map((row) =>
        row.rowId === rowId ? { ...row, ...changes } : row
      ),
    })

  return (
    <Card>
      <Form
        className="ant-advanced-search-form"
        layout="vertical"
        onFinish={() => {
          if (!searchDisabled) onSearch()
        }}
      >
        <div style={{ width: 'max-content', maxWidth: '100%' }}>
          <Row align="bottom" gutter={48} style={ROW_STYLE}>
            <Col flex="auto" style={{ ...COLUMN_STYLE, maxWidth: '100%' }}>
              <Form.Item
                help={
                  dateErrorKey ? (
                    <span id="hk-mtd-base-date-error">{t(dateErrorKey)}</span>
                  ) : undefined
                }
                htmlFor="hk-mtd-base-date"
                label={t('hkReport.baseDate')}
                validateStatus={dateErrorKey ? 'error' : undefined}
              >
                <Input.Group compact style={{ width: 320, maxWidth: '100%' }}>
                  <Form.Item noStyle>
                    <Select
                      aria-label={t('hkReport.baseDateMode')}
                      disabled={conditionsDisabled}
                      onChange={setDateMode}
                      style={{ width: '30%' }}
                      value={dateMode}
                    >
                      <Select.Option value="Monthly">
                        {t('hkReport.monthly')}
                      </Select.Option>
                      <Select.Option value="AllDate">
                        {t('hkReport.allDate')}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item noStyle>
                    <DatePicker
                      allowClear={false}
                      aria-describedby={
                        dateErrorKey ? 'hk-mtd-base-date-error' : undefined
                      }
                      aria-invalid={Boolean(dateErrorKey)}
                      aria-label={t('hkReport.baseDate')}
                      className="date-picker-nav"
                      disabled={conditionsDisabled}
                      id="hk-mtd-base-date"
                      inputReadOnly
                      onChange={(date) =>
                        onChange({
                          ...value,
                          baseDate: date
                            ? (dateMode === 'Monthly'
                                ? date.clone().endOf('month')
                                : date
                              ).format('YYYY-MM-DD')
                            : '',
                        })
                      }
                      picker={dateMode === 'Monthly' ? 'month' : 'date'}
                      value={parsedDate.isValid() ? parsedDate : null}
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col flex="none" style={COLUMN_STYLE}>
              <Form.Item>
                <div style={OPTION_STYLE}>
                  <span>{t('hkReport.groupBy')}</span>
                  <Radio.Group
                    aria-label={t('hkReport.groupBy')}
                    disabled={conditionsDisabled}
                    onChange={(event) =>
                      onChange({ ...value, groupBy: event.target.value })
                    }
                    value={value.groupBy}
                  >
                    <Radio value="Currency">{t('hkReport.currency')}</Radio>
                    <Radio value="Class">{t('hkReport.class')}</Radio>
                  </Radio.Group>
                </div>
              </Form.Item>
            </Col>
            <Col flex="none" style={COLUMN_STYLE}>
              <Form.Item>
                <div style={OPTION_STYLE}>
                  <label htmlFor="hk-mtd-use-account-mtd">
                    {t('hkReport.useAccountMtd')}
                  </label>
                  <Checkbox
                    aria-label={t('hkReport.useAccountMtd')}
                    checked={value.isAppliedAccountMTD}
                    disabled={conditionsDisabled}
                    id="hk-mtd-use-account-mtd"
                    onChange={(event) =>
                      onChange({
                        ...value,
                        isAppliedAccountMTD: event.target.checked,
                      })
                    }
                  />
                </div>
              </Form.Item>
            </Col>
            <Col flex="none" style={COLUMN_STYLE}>
              <Form.Item>
                <div style={OPTION_STYLE}>
                  <label htmlFor="hk-mtd-use-full-fund-name">
                    {t('hkReport.useFullFundName')}
                  </label>
                  <Checkbox
                    aria-label={t('hkReport.useFullFundName')}
                    checked={value.useFullFundName}
                    disabled={conditionsDisabled}
                    id="hk-mtd-use-full-fund-name"
                    onChange={(event) =>
                      onChange({
                        ...value,
                        useFullFundName: event.target.checked,
                      })
                    }
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        </div>
        {value.rows.map((row, index) => {
          const rowErrors = errors.rows[row.rowId]
          const portfolioErrorKey = rowErrors?.portfolioIds
            ? rowErrors.portfolioIds === 'unavailable'
              ? 'hkReport.error.portfolioUnavailable'
              : 'hkReport.error.portfolioDuplicate'
            : index === 0 && errors.portfolios
            ? 'hkReport.error.portfolioRequired'
            : undefined
          const benchmarkErrorKey = rowErrors?.benchmarkInput
            ? BENCHMARK_ERROR_KEYS[rowErrors.benchmarkInput]
            : undefined
          const selectedInOtherRows = new Set(
            value.rows
              .filter((other) => other.rowId !== row.rowId)
              .flatMap((other) => other.portfolioIds)
          )
          const portfolioId = `hk-mtd-${row.rowId}-portfolio`
          const benchmarkId = `hk-mtd-${row.rowId}-benchmark`
          const portfolioLabel = `${t('hkReport.portfolio')} ${index + 1}`
          const benchmarkLabel = `${t('hkReport.benchmark')} ${index + 1}`

          return (
            <Row
              className="hk-mtd-condition-row"
              gutter={[24, 0]}
              key={row.rowId}
              style={ROW_STYLE}
            >
              <Col md={12} style={COLUMN_STYLE} xs={24}>
                <Form.Item
                  help={
                    portfolioErrorKey ? (
                      <span id={`${portfolioId}-error`}>
                        {t(portfolioErrorKey)}
                      </span>
                    ) : undefined
                  }
                  htmlFor={portfolioId}
                  label={portfolioLabel}
                  validateStatus={portfolioErrorKey ? 'error' : undefined}
                >
                  <Select<number[]>
                    aria-describedby={
                      portfolioErrorKey ? `${portfolioId}-error` : undefined
                    }
                    aria-invalid={Boolean(portfolioErrorKey)}
                    aria-label={portfolioLabel}
                    disabled={portfolioSelectionDisabled || conditionsDisabled}
                    id={portfolioId}
                    maxTagCount="responsive"
                    mode="multiple"
                    onChange={(portfolioIds) =>
                      updateRow(row.rowId, { portfolioIds })
                    }
                    optionFilterProp="label"
                    options={portfolios.map((portfolio) => ({
                      label: portfolio.name,
                      value: portfolio.id,
                      disabled:
                        selectedInOtherRows.has(portfolio.id) &&
                        !row.portfolioIds.includes(portfolio.id),
                    }))}
                    style={INPUT_STYLE}
                    value={row.portfolioIds}
                  />
                </Form.Item>
              </Col>
              <Col md={12} style={COLUMN_STYLE} xs={24}>
                <Form.Item
                  help={
                    benchmarkErrorKey ? (
                      <span id={`${benchmarkId}-error`}>
                        {t(benchmarkErrorKey)}
                      </span>
                    ) : undefined
                  }
                  htmlFor={benchmarkId}
                  label={benchmarkLabel}
                  validateStatus={benchmarkErrorKey ? 'error' : undefined}
                >
                  <div style={OPTION_STYLE}>
                    {/* 入力途中や検証対象の値を、blur 時も自動補正しない。 */}
                    <InputNumber<string>
                      addonAfter="%"
                      aria-describedby={
                        benchmarkErrorKey ? `${benchmarkId}-error` : undefined
                      }
                      aria-invalid={Boolean(benchmarkErrorKey)}
                      aria-label={benchmarkLabel}
                      controls={false}
                      disabled={conditionsDisabled}
                      formatter={() => row.benchmarkInput}
                      id={benchmarkId}
                      keyboard={false}
                      onChange={(benchmarkInput) =>
                        updateRow(row.rowId, {
                          benchmarkInput: benchmarkInput ?? '',
                        })
                      }
                      onInput={(benchmarkInput) =>
                        updateRow(row.rowId, { benchmarkInput })
                      }
                      parser={(input) => input ?? ''}
                      stringMode
                      style={BENCHMARK_STYLE}
                      value={row.benchmarkInput || null}
                    />
                    <Button
                      aria-label={`${t('hkReport.removeRow')} ${index + 1}`}
                      danger
                      disabled={conditionsDisabled || value.rows.length <= 1}
                      icon={<DeleteOutlined />}
                      onClick={() => onRemoveRow(row.rowId)}
                      type="text"
                    />
                  </div>
                </Form.Item>
              </Col>
            </Row>
          )
        })}
        <Button
          aria-label={t('hkReport.addRow')}
          disabled={conditionsDisabled || value.rows.length >= 6}
          icon={<PlusOutlined />}
          onClick={onAddRow}
          style={{ marginBottom: 24 }}
        >
          {t('hkReport.addRow')}
        </Button>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            aria-label={t('hkReport.search')}
            disabled={searchDisabled}
            htmlType="submit"
            loading={searchLoading}
            type="primary"
          >
            {t('hkReport.search')}
          </Button>
        </div>
      </Form>
    </Card>
  )
}

export default SearchConditions
